/**
 * Browser-side note sync hook
 * Handles background synchronization between IndexedDB and server
 * Supports offline-first with automatic retry and conflict resolution
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { StoredNote, NotePayload, NoteProof, Hash } from '@maco144/shared';
import type { ReturnType as NoteStoreReturnType } from './use-note-store';

export type ConflictStrategy = 'server-wins' | 'client-wins' | 'manual';

export interface NoteSyncState {
  isSyncing: boolean;
  lastSyncTime: number;
  worldRootAtSync: string;
  pendingChanges: Array<{
    noteId: string;
    operation: 'create' | 'update' | 'delete' | 'plant' | 'unplant';
    timestamp: number;
    data?: NotePayload;
  }>;
  failedChanges: Array<{
    noteId: string;
    operation: string;
    error: string;
    retryCount: number;
  }>;
  syncError: string | null;
}

export interface NoteSyncOptions {
  syncInterval?: number; // ms between syncs (default: 5000)
  retryAttempts?: number; // max retries per change (default: 3)
  retryDelay?: number; // ms delay between retries (default: 1000)
  conflictStrategy?: ConflictStrategy; // default: 'server-wins'
  enabled?: boolean; // enable/disable syncing (default: true)
}

/**
 * Hook for syncing notes between local cache and server
 * Runs in background and handles network errors gracefully
 */
export function useNoteSync(
  store: ReturnType<typeof import('./use-note-store').useNoteStore> | null,
  userId?: string,
  wsUrl?: string,
  options: NoteSyncOptions = {}
) {
  const {
    syncInterval = 5000,
    retryAttempts = 3,
    retryDelay = 1000,
    conflictStrategy = 'server-wins',
    enabled = true,
  } = options;

  const [syncState, setSyncState] = useState<NoteSyncState>({
    isSyncing: false,
    lastSyncTime: 0,
    worldRootAtSync: '',
    pendingChanges: [],
    failedChanges: [],
    syncError: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const syncTimerRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountersRef = useRef<Map<string, number>>(new Map());
  const offlineLoggedRef = useRef(false);

  const storeRef = useRef(store);
  storeRef.current = store;
  const pendingChangesRef = useRef<NoteSyncState['pendingChanges']>([]);
  pendingChangesRef.current = syncState.pendingChanges;

  // Track local changes for sync
  const addPendingChange = useCallback(
    (
      noteId: string,
      operation: 'create' | 'update' | 'delete' | 'plant' | 'unplant',
      data?: NotePayload
    ) => {
      setSyncState((prev) => {
        // Remove from failed if it was there
        const failed = prev.failedChanges.filter((c) => c.noteId !== noteId);

        // Add/update in pending
        const pending = prev.pendingChanges.filter((c) => c.noteId !== noteId);
        pending.push({
          noteId,
          operation,
          timestamp: Date.now(),
          data,
        });

        return {
          ...prev,
          pendingChanges: pending,
          failedChanges: failed,
        };
      });
    },
    []
  );

  // Connect to WebSocket
  const connectWebSocket = useCallback(async () => {
    if (!wsUrl || !userId) return;

    return new Promise<WebSocket | null>((resolve) => {
      try {
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('[NoteSync] WebSocket connected ✓');
          offlineLoggedRef.current = false;
          wsRef.current = ws;
          resolve(ws);
        };

        ws.onerror = () => {
          if (!offlineLoggedRef.current) {
            console.log('[NoteSync] Server unavailable - using local-only mode');
            offlineLoggedRef.current = true;
          }
          resolve(null);
        };

        ws.onclose = () => {
          wsRef.current = null;
        };

        // Set timeout for connection
        setTimeout(() => {
          if (!wsRef.current) {
            if (!offlineLoggedRef.current) {
              console.log('[NoteSync] Server unavailable - using local-only mode');
              offlineLoggedRef.current = true;
            }
            resolve(null);
          }
        }, 5000);
      } catch (err) {
        if (!offlineLoggedRef.current) {
          console.log('[NoteSync] Server unavailable - using local-only mode');
          offlineLoggedRef.current = true;
        }
        resolve(null);
      }
    });
  }, [wsUrl, userId]);

  // Send message to server
  const sendMessage = useCallback((message: unknown) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('WebSocket not connected'));
    }

    return new Promise<void>((resolve, reject) => {
      try {
        wsRef.current!.send(JSON.stringify(message));
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  // Resolve conflict between local and server versions
  const resolveConflict = useCallback(
    (local: StoredNote, server: StoredNote): StoredNote => {
      switch (conflictStrategy) {
        case 'server-wins':
          return server;
        case 'client-wins':
          return local;
        case 'manual':
          // Manual resolution: prefer newer timestamp
          if (local.updatedAt && server.updatedAt) {
            return local.updatedAt > server.updatedAt ? local : server;
          }
          return server;
        default:
          return server;
      }
    },
    [conflictStrategy]
  );

  // Sync local changes to server
  const syncToServer = useCallback(async () => {
    if (!storeRef.current || !userId || !enabled) return;

    setSyncState((prev) => ({ ...prev, isSyncing: true }));

    try {
      // Try to connect if not already connected
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await connectWebSocket();
      }

      // If no server available, just stay pending (offline mode)
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        setSyncState((prev) => ({ ...prev, isSyncing: false }));
        return;
      }

      const pendingChanges = pendingChangesRef.current;

      for (const change of pendingChanges) {
        const retryCount = retryCountersRef.current.get(change.noteId) || 0;

        if (retryCount >= retryAttempts) {
          // Max retries exceeded, move to failed
          setSyncState((prev) => ({
            ...prev,
            pendingChanges: prev.pendingChanges.filter((c) => c.noteId !== change.noteId),
            failedChanges: [
              ...prev.failedChanges,
              {
                noteId: change.noteId,
                operation: change.operation,
                error: 'Max retries exceeded',
                retryCount,
              },
            ],
          }));
          continue;
        }

        try {
          // Send to server based on operation
          const message: any = {
            userId,
            noteId: change.noteId,
          };

          switch (change.operation) {
            case 'create':
              message.type = 'create_note';
              message.payload = change.data;
              break;
            case 'update':
              message.type = 'update_note';
              message.payload = change.data;
              break;
            case 'delete':
              message.type = 'delete_note';
              break;
            case 'plant':
              message.type = 'plant_note';
              if (change.data?.floorPosition) {
                message.floorPosition = change.data.floorPosition;
                message.visualType = change.data.visualType || 'sticky';
              }
              break;
            case 'unplant':
              message.type = 'unplant_note';
              break;
          }

          await sendMessage(message);

          // Mark as synced
          setSyncState((prev) => ({
            ...prev,
            pendingChanges: prev.pendingChanges.filter((c) => c.noteId !== change.noteId),
            lastSyncTime: Date.now(),
            syncError: null,
          }));

          retryCountersRef.current.delete(change.noteId);
        } catch (err) {
          // Increment retry count
          const newRetryCount = retryCount + 1;
          retryCountersRef.current.set(change.noteId, newRetryCount);

          console.warn(
            `[NoteSync] Failed to sync ${change.noteId}, retry ${newRetryCount}/${retryAttempts}:`,
            err
          );
        }
      }

      setSyncState((prev) => ({ ...prev, isSyncing: false }));
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Unknown error';
      setSyncState((prev) => ({
        ...prev,
        isSyncing: false,
        syncError: error,
      }));

      console.error('[NoteSync] Sync failed:', error);
    }
  }, [
    userId,
    enabled,
    connectWebSocket,
    sendMessage,
    retryAttempts,
  ]);

  // Sync server changes to local
  const syncFromServer = useCallback(async () => {
    if (!storeRef.current || !userId || !enabled) return;

    try {
      // Try to connect if not already connected
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        await connectWebSocket();
      }

      // Skip if no server available (offline mode)
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return;
      }

      // Request all notes from server
      await sendMessage({
        type: 'get_notes',
        userId,
      });

      // Server will respond with notes_loaded message
    } catch (err) {
      // Silently ignore - expected in offline mode
    }
  }, [userId, enabled, connectWebSocket, sendMessage]);

  // Handle messages from server
  const handleServerMessage = useCallback(
    async (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as any;

        switch (message.type) {
          case 'note_created':
          case 'note_updated': {
            // Server confirmed our update
            const { worldRoot } = message;
            setSyncState((prev) => ({
              ...prev,
              worldRootAtSync: worldRoot,
            }));
            break;
          }

          case 'notes_loaded': {
            // Server sent notes (bulk load)
            const { notes } = message;
            if (storeRef.current && notes) {
              for (const noteData of notes) {
                // Resolve conflicts if note exists locally
                const local = await storeRef.current.getNote(noteData.noteId);
                const resolved = local ? resolveConflict(local, noteData) : noteData;

                // Update local store
                await storeRef.current.setNote(noteData.noteId, resolved, userId!);
              }

              setSyncState((prev) => ({
                ...prev,
                lastSyncTime: Date.now(),
              }));
            }
            break;
          }

          case 'sync_complete': {
            // Server acknowledged sync
            const { worldRoot } = message;
            setSyncState((prev) => ({
              ...prev,
              lastSyncTime: Date.now(),
              worldRootAtSync: worldRoot,
            }));
            break;
          }

          case 'sync_error': {
            const { error } = message;
            setSyncState((prev) => ({
              ...prev,
              syncError: error,
            }));
            console.error('[NoteSync] Server error:', error);
            break;
          }
        }
      } catch (err) {
        console.error('[NoteSync] Failed to handle message:', err);
      }
    },
    [userId, resolveConflict]
  );

  // Setup WebSocket message listener
  useEffect(() => {
    if (!wsRef.current) return;

    wsRef.current.addEventListener('message', handleServerMessage);

    return () => {
      if (wsRef.current) {
        wsRef.current.removeEventListener('message', handleServerMessage);
      }
    };
  }, [handleServerMessage]);

  // Setup periodic sync
  useEffect(() => {
    if (!enabled || !userId || !storeRef.current) return;

    const sync = async () => {
      if (!navigator.onLine) {
        return; // Skip sync if offline
      }

      try {
        // Sync both directions
        await syncFromServer();
        await syncToServer();
      } catch (err) {
        console.error('[NoteSync] Periodic sync failed:', err);
      }
    };

    // Initial sync
    sync();

    // Periodic syncs
    syncTimerRef.current = setInterval(sync, syncInterval);

    return () => {
      if (syncTimerRef.current) {
        clearInterval(syncTimerRef.current);
      }
    };
  }, [enabled, userId, syncInterval, syncToServer, syncFromServer]);

  // Sync when going online
  useEffect(() => {
    const handleOnline = async () => {
      console.log('[NoteSync] Online, starting sync');
      await syncToServer();
      await syncFromServer();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncToServer, syncFromServer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (syncTimerRef.current) {
        clearInterval(syncTimerRef.current);
      }
    };
  }, []);

  return {
    // State
    isSyncing: syncState.isSyncing,
    lastSyncTime: syncState.lastSyncTime,
    worldRootAtSync: syncState.worldRootAtSync,
    pendingChanges: syncState.pendingChanges,
    failedChanges: syncState.failedChanges,
    syncError: syncState.syncError,

    // Operations
    addPendingChange,
    syncToServer,
    syncFromServer,
  };
}
